// Ghost musicians: when the planet is quiet, the Durable Object summons a few
// generative players who live at real Cloudflare data centers and improvise
// on the shared loop. They are deterministic given a seed so they are testable.

import { COLOS, type Colo } from "./colos";
import type { ColoRegion } from "./colo-data";
import { LANES, STEPS, type VoiceName } from "./music";
import type { Cell, PlayerInfo } from "./protocol";
import { cellKey } from "./protocol";
import { hash32, hueFor, rng } from "./names";

export const REGION_VOICE: Record<ColoRegion, VoiceName> = {
  EU: "glass",
  NA: "pluck",
  SA: "marimba",
  AF: "kalimba",
  AP: "koto",
  ME: "koto",
  OC: "flute",
};

const REGION_CONTINENT: Record<ColoRegion, string> = {
  EU: "EU",
  NA: "NA",
  SA: "SA",
  AF: "AF",
  AP: "AS",
  ME: "AS",
  OC: "OC",
};

/** Rhythmic "gravity": positions a ghost prefers, strongest first. */
const RHYTHM_WEIGHTS = [1, 0.15, 0.35, 0.5, 0.75, 0.15, 0.55, 0.2, 0.9, 0.15, 0.5, 0.3, 0.7, 0.2, 0.45, 0.25];

export const GHOST_TTL_BARS = 28;
export const GHOST_MAX_CELLS = 26;

export interface GhostState {
  player: PlayerInfo;
  lastLane: number;
  /** Bar at which the ghost fades away. */
  leaveBar: number;
}

export function ghostPlayer(colo: Colo, seed: string, bar: number): PlayerInfo {
  return {
    id: `ghost-${colo.iata.toLowerCase()}-${hash32(seed).toString(36)}`,
    name: `Ghost of ${colo.city}`,
    hue: hueFor(seed),
    voice: REGION_VOICE[colo.region],
    lat: colo.lat,
    lon: colo.lon,
    city: colo.city,
    country: colo.country,
    continent: REGION_CONTINENT[colo.region],
    colo: colo.iata,
    ghost: true,
    joinedAt: bar,
  };
}

/** Picks a ghost at a data center not already in use, preferring far-away regions. */
export function summonGhost(
  bar: number,
  roomSeed: string,
  taken: ReadonlySet<string>,
  lifetimeBars = 48,
): GhostState {
  const r = rng(hash32(`${roomSeed}:${bar}:${taken.size}`));
  let colo = COLOS[Math.floor(r() * COLOS.length)]!;
  for (let i = 0; i < 12 && taken.has(colo.iata); i++) colo = COLOS[Math.floor(r() * COLOS.length)]!;
  const player = ghostPlayer(colo, `${roomSeed}:${bar}:${colo.iata}`, bar);
  return {
    player,
    lastLane: Math.floor(r() * LANES),
    leaveBar: bar + lifetimeBars + Math.floor(r() * lifetimeBars),
  };
}

/**
 * One improvisation move for a ghost. Returns the cell to place (or null to rest)
 * and optionally a key to remove (ghosts tidy up their oldest notes).
 */
export function ghostMove(
  g: GhostState,
  cells: ReadonlyMap<number, Cell>,
  bar: number,
  random: () => number,
): { place: Cell | null; remove: number | null } {
  const mine = [...cells.entries()].filter(([, c]) => c.owner === g.player.id);
  let remove: number | null = null;
  if (mine.length > 5 && random() < 0.5) {
    mine.sort((a, b) => a[1].born - b[1].born);
    remove = mine[0]![0];
  }
  if (cells.size >= GHOST_MAX_CELLS || random() < 0.35) return { place: null, remove };

  // Melodic random walk with a pull toward the middle of the range.
  const center = (LANES - 1) / 2;
  const pull = (center - g.lastLane) * 0.25;
  const leap = [-2, -1, -1, 0, 1, 1, 2][Math.floor(random() * 7)]!;
  let lane = Math.round(g.lastLane + leap + pull * random());
  lane = Math.max(0, Math.min(LANES - 1, lane));

  // Choose a step weighted by rhythmic gravity, avoiding occupied cells.
  let total = 0;
  const weights: number[] = [];
  for (let s = 0; s < STEPS; s++) {
    const w = cells.has(cellKey(s, lane)) ? 0 : RHYTHM_WEIGHTS[s]!;
    weights.push(w);
    total += w;
  }
  if (total <= 0) return { place: null, remove };
  let pick = random() * total;
  let step = 0;
  for (; step < STEPS - 1; step++) {
    pick -= weights[step]!;
    if (pick <= 0) break;
  }
  g.lastLane = lane;
  const p = g.player;
  return {
    place: {
      step,
      lane,
      owner: p.id,
      ownerName: p.name,
      hue: p.hue,
      voice: p.voice,
      lat: p.lat,
      lon: p.lon,
      colo: p.colo,
      born: bar,
      ttl: GHOST_TTL_BARS,
    },
    remove,
  };
}

/** How many ghosts a room wants given the number of humans. */
export function desiredGhosts(humans: number): number {
  if (humans <= 0) return 0;
  if (humans === 1) return 3;
  if (humans === 2) return 2;
  if (humans <= 4) return 1;
  return 0;
}
